import React from 'react';
import { injectIntl } from 'react-intl';
import MakeHash from 'make-hash';
import classNames from 'classnames';
import moment from 'moment';
import getParamByName from 'utils/get-param-by-name';
import GridCol from 'components/grid/grid-col';
import GridRow from 'components/grid/grid-row';
import TransitionLink from 'components/transition-link/transition-link';
import Hero from 'components/heroes/hero';
import ProductMatch from 'pages/product-matches/_product-match';
import LastSessionModule from 'pages/product-matches/_last-session-module';
import Page from 'components/page/page';
import ProductMatchesData from 'pages/product-matches/product-matches-data';
import LoaderHelper from 'helpers/utilities/loader-helper';
import ProductTypeFilter from 'pages/product-matches/_product-type-filter';
import ProductCategoryFilter from 'pages/product-matches/_product-category-filter';
import ProductGenderToggle from 'pages/product-matches/_product-gender-toggle';
import messages from './messages';

/**
 * The ProductMatches page requires 4 different API calls so there is a large amount of logic needed
 * to determine when the page is ready/in server error/empty
 *
 * So we extend ProductMatchesData here to abstract all of the API logic out of this component.
 * This isn't normal practice, but is done for readability.
 */
class ProductMatches extends ProductMatchesData {
  constructor(props) {
    super(props);

    const { intl } = props;

    this.state = {
      'selectedProductType': this.setInitialProductType(props),
      'selectedProfile': undefined,
      'genderFilterEnabled': true,
      'loading': true,
      'serverError': false,
      'shoesLoaded': false,
      'shoeMatches': undefined,
      'saddlesLoaded': false,
      'saddleMatches': undefined,
      'bikesLoaded': false,
      'bikeMatches': undefined,
      'sessionLoaded': false,
      'sessionData': undefined,
      'recommendedSizing': {}
    };

    this.page = {
      'heading': intl.formatMessage(messages.heading),
      'intro': intl.formatMessage(messages.intro),
      'error': intl.formatMessage(messages.load_error)
    };

    /**
     * Flag to track the mounted state of the component so setState is not
     * called on a component that has been destroyed
     *
     * @type {Boolean}
     */
    this._isMounted = false;

    this.setUpHandlers();
  }

  /**
   * Binds any handlers to the correct context
   *
   * @method  setUpHandlers
   */
  setUpHandlers() {
    this.onServerError = this.handleServerError.bind(this);
    this.onMatchFilter = this.handleMatchFilter.bind(this);
    this.onCategoryChange = this.handleCategoryChange.bind(this);
    this.onGenderToggle = this.handleGenderToggle.bind(this);
  }

  /**
   * React Lifecycle Hook
   * Invoked once, immediately before mounting occurs. It is called before
   * render(), therefore setting state synchronously in this method will not
   * trigger a re-rendering.
   *
   * @method  componentWillMount
   */
  componentWillMount() {
    this.startTime = moment();
    this.getSessionData().then(() => {
      this.removeLoader();
    });
  }

  /**
   * React Lifecycle Hook
   * Invoked once, immediately after the initial rendering occurs.
   *
   * @method  componentDidMount
   */
  componentDidMount() {
    this._isMounted = true;
  }

  /**
   * React Lifecycle Hook
   * Invoked immediately before a component is unmounted and destroyed.
   * Perform any necessary cleanup in this method
   *
   * @method  componentWillUnmount
   */
  componentWillUnmount() {
    this._isMounted = false;

    this.toggleThemes('dark');
  }

  setInitialProductType(props) {
    return props.history.location.search ? getParamByName('selectedProductType', props.history.location.search) : undefined;
  }

  /**
   * Retrieves the selected products based on the currently selected product type.
   * Returns empty array when no products are found for the product type.
   *
   * @method selectedProducts
   * @return {Array} [The list of products based on product type]
   */
  selectedProducts() {
    const { selectedProductType } = this.state;
    const products = this.state[`${selectedProductType}Matches`] || [];

    return this.applyFilters(products);
  }

  /**
   * Retrieves the session data based on the selected product type. Returns empty
   * session when no data found for product type.
   *
   * @method selectedSession
   * @return {Object} [Session data based on the product type]
   */
  selectedSession() {
    const { selectedProductType } = this.state;

    return selectedProductType ? this.state.sessionData.find(el => el.type === selectedProductType) : {}
  }

  toggleThemes(theme) {
    if (this.contentLoaded()) {
      this.props.toggleSiteTheme(theme);
      this.props.toggleFooterTheme(theme);
    }
  }

  handleServerError(error) {
    if (this.state.serverError) return;

    if (this.allRequestsFailed()) {
      this.setState({
        'serverError': true,
        'loading': false
      });

      this.toggleThemes('dark');
    }
  }

  handleMatchFilter(filterValue) {
    this.setState({ 'selectedProductType': filterValue });
  }

  /**
   * Updates state when category is changed and re-fetches product data from
   * the server based on the new category
   *
   * @method handleCategoryChange
   * @param  {Object} category [The new category selected]
   */
  handleCategoryChange(category) {
    this.setState({ 'loading': true, 'selectedCategory': category }, () => {
      this.getProductData().then(() => {
        this.setState({ 'loading': false });
      });
    });
  }

  /**
   * Handles updates to the gender filter by toggling the internal component state
   *
   * @method handleGenderChange
   * @param  {Object} isToggled [Whether the gender filter is on or off]
   */
  handleGenderToggle(isToggled) {
    this.setState({ 'genderFilterEnabled': isToggled });
  }

  /**
   * Filters the product matches based on the current filter settings
   *
   * @method applyFilters
   * @param  {Array} products [Array of product matches to filter]
   * @return {Array}          [Filtered set of products]
   */
  applyFilters(products) {
    if (!this.state.genderFilterEnabled) {
      return products;
    }

    const { gender: sessionGender } = this.selectedSession();

    return products.filter(product => {
      return (
        product.gender === sessionGender
        || product.gender === 'unisex'
      )
    });
  }

  _renderHeroSection() {
    const supportingTxt = this.state.serverError ? this.page.error : this.page.intro;

    return (
      <Hero title={this.page.heading}
        supportingTxt={supportingTxt}
        serverError={this.state.serverError}
      />
    );
  }

  _renderFilters() {
    const { intl } = this.props;
    const { gender } = this.selectedSession();

    return (
      <GridRow className="mix-grid_vertCenter">
        <GridCol
          className="mix-cell_noGutterMobile"
          desktop="9"
          phone="4"
          tablet="5"
          tabletLg="5"
        >
          <div className="block mix-block_justifyLeft">
            <div className="block-item">
              <ProductCategoryFilter
                selectedCategory={this.state.selectedCategory}
                onCategoryChange={this.onCategoryChange}
              />
            </div>
            <div className="block-item">
              <ProductTypeFilter
                selectedProductType={this.state.selectedProductType}
                onProductTypeChange={this.onMatchFilter}
                bike={this.state.bikeMatches}
                shoe={this.state.shoeMatches}
                saddle={this.state.saddleMatches}
              />
            </div>
          </div>
        </GridCol>
        <GridCol
          className="cell_detachedSm mix-cell_noGutterMobile"
          desktop="3"
          phone="4"
          tablet="3"
          tabletLg="3"
        >
          <div className="staggered_right">
            <ProductGenderToggle
              isToggled={this.state.genderFilterEnabled}
              offText={intl.formatMessage(messages.gender_filter_all_label)}
              onText={intl.formatMessage(messages[`gender_filter_${gender}_label`])}
              onToggle={this.onGenderToggle}
            />
          </div>
        </GridCol>
      </GridRow>
    );
  }

  _renderBody() {
    if (!this.contentLoaded() || this.state.serverError) return;

    return (
      <div className="container container_wide">
        <section className="tier">
          <div className="set">
            <div className="set-hd">
              {this._renderFilters()}
            </div>

            <div className="set-bd set-bd_mega">
              <LastSessionModule
                sessionData={this.selectedSession()}
                sizingData={this.state.recommendedSizing}
                type={this.state.selectedProductType}
                toggleDialog={this.props.toggleDialog}
              />
            </div>

            {this.selectedProducts().map((product, i) => {
              return(
                <ProductMatch
                  key={MakeHash(product.id)}
                  index={i}
                  data={product}
                />
              );
            })}
          </div>
        </section>
      </div>
    )
  }

  removeLoader() {
    LoaderHelper(this.startTime, () => {
      if (this._isMounted) {
        this.setState({
          'loading': false
        });
      }
    });
  }

  render() {
    const pageHero = this._renderHeroSection();

    return (
      <Page
        isLoading={this.state.loading}
        title={this.page.heading}
        fixedLoader
        fullTexture={!this.contentLoaded()}
        pageHero={pageHero}
        mainClasses="page-main_megaTop page-main_themeLight"
      >
        {this._renderBody()}
      </Page>
    );
  }
}

export default injectIntl(ProductMatches);



// WEBPACK FOOTER //
// ./src/client/pages/product-matches/product-matches.jsx